import {MutableRefObject, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {Playlist} from '../../../../../../../backend/interfaces/playlist';
import {PlaylistItem} from '../../../../../../../backend/interfaces/playlist-item';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';

// Columns IDs
enum ColumnIdentifier {
  Title = 'title',
  State = 'state',
}

export default function EntityPlaylistItemsSystemAdministratorOfficeScreen() {
  // Get params
  // - id: entity id
  // - playlistId: playlist id
  let {id, playlistId}: {id?: string; playlistId?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [playlist, setPlaylist] = useState<Playlist | undefined>();
  const [updateItemDialogOpened, setUpdateItemDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {itemId: string; itemName: string; itemActiveFlag: boolean} | undefined
  > = useRef();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Validate and get the playlist
  useEffect(() => {
    const getPlaylist = async () => {
      try {
        // Check id
        if (!playlistId) {
          throw new Error('Empty playlist id.');
        }

        // Get the playlist
        const result = await PlaylistsRepository.instance.getPlaylistByIdAsSystemAdministrator(
          playlistId,
          true
        );

        if (result) {
          // Set playlist
          setPlaylist(result);
        } else {
          // Error
          throw new Error('Empty playlist.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the playlist.', error);

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.entities.edit.playlist_items.errors.playlist_does_not_exist_error
          ),
        });

        // Redirect
        history.push(`${url.replace(`/playlists/${playlistId}`, '/playlists')}`);
      }
    };

    // Get the playlist
    getPlaylist();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  // Fetch items
  const handleGetItems = async (limit: number, skip: number, search?: string) => {
    // Check ids
    if (!playlistId || !id) {
      throw new Error('Empty entity or playlist id.');
    }

    // Load playlist items
    const result: {totalCount: number; data: Array<PlaylistItem>} =
      await PlaylistsRepository.instance.getPlaylistItemsInEntityAsSystemAdministrator(
        id,
        playlistId,
        skip,
        limit,
        search,
        true
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItem> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.thumbnailUrl,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Playlists}/${playlistId}/items/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Title,
            // The title is localized
            value: obj.title || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.entities.edit.playlist_items.state_value_true)
              : t(translations.entities.edit.playlist_items.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistItem>({
    fetchData: handleGetItems,
  });

  const handleAskUpdateItemState = (itemId: string, value: boolean) => {
    // Get item name
    const itemName: string | undefined = data
      .find((obj) => obj.id === itemId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Title)?.value;

    // Set item info for later
    dataToUpdateRef.current = {
      itemId: itemId,
      itemName: itemName || '',
      itemActiveFlag: value,
    };

    // Open modal
    setUpdateItemDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdateItemState = () => {
    // Close modal
    setUpdateItemDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateItemState = () => {
    // Close modal
    setUpdateItemDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateItemState();
  };

  const handleUpdateItemState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const itemId = dataToUpdateRef.current.itemId;
      const value = dataToUpdateRef.current.itemActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === itemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        itemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.entities.edit.playlist_items.state_value_true)
              : t(translations.entities.edit.playlist_items.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateItemState(id || '', playlistId || '', itemId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.entities.edit.playlist_items.errors.update_state_item_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist item. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateItemState = async (
    entityId: string,
    playlistId: string,
    playlistItemId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistItemActiveStateForEntityAsSystemAdministrator(
        entityId,
        playlistId,
        playlistItemId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistItemId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        playlistItemId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.entities.edit.playlist_items.state_value_true)
              : t(translations.entities.edit.playlist_items.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.entities.edit.playlist_items.errors.enable_item_error_message)
          : t(translations.entities.edit.playlist_items.errors.disable_item_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist item with id ${playlistItemId} to ${value} for entity with id ${entityId}.`,
        error
      );
    }
  };

  return (
    <ContentPage>
      <PageHeader
        title={t(translations.entities.edit.playlist_items.title, {
          name: playlist?.name,
        })}
        titleAvatar={{type: 'other', url: playlist?.imageUrl}}
        isSmallTitle={true}
        showSearch={true}
        searchLabel={t(translations.entities.edit.playlist_items.search_input_placeholder)}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
      />
      <PaginatedTable
        avatar={{
          type: 'other',
        }}
        emptyTableMessage={t(translations.entities.edit.playlist_items.no_items)}
        columns={[
          {
            columnId: ColumnIdentifier.Title,
            title: t(translations.entities.edit.playlist_items.title_header),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.entities.edit.playlist_items.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.entities.edit.playlist_items.state_value_true),
                handleClick: handleAskUpdateItemState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <AlertModal
        id="confirm-disable-playlist-item-state-entity"
        opened={updateItemDialogOpened.disableDialogOpened}
        title={t(translations.entities.edit.playlist_items.disable_item_dialog_title, {
          name: dataToUpdateRef.current?.itemName || '',
        })}
        content={t(translations.entities.edit.playlist_items.disable_item_dialog_message, {
          name: officeContext.state.entityForm?.name || '',
        })}
        handleClose={handleDeclineUpdateItemState}
        actions={[
          {
            label: t(
              translations.entities.edit.playlist_items.disable_item_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdateItemState,
          },
          {
            label: t(
              translations.entities.edit.playlist_items.disable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdateItemState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-item-state-entity"
        opened={updateItemDialogOpened.enableDialogOpened}
        title={t(translations.entities.edit.playlist_items.enable_item_dialog_title, {
          name: dataToUpdateRef.current?.itemName || '',
        })}
        content={t(translations.entities.edit.playlist_items.enable_item_dialog_message, {
          name: officeContext.state.entityForm?.name || '',
        })}
        handleClose={handleDeclineUpdateItemState}
        actions={[
          {
            label: t(translations.entities.edit.playlist_items.enable_item_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleDeclineUpdateItemState,
          },
          {
            label: t(
              translations.entities.edit.playlist_items.enable_item_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdateItemState,
          },
        ]}
      />
    </ContentPage>
  );
}
